import '@/components/ChatWidget/styles.scss';

import { Badge, Stack } from 'react-bootstrap';
import { DATETIME_RFC_FORMAT, TIMEZONE_OFFSET, TODAY, TODAY_EOD } from '@/constants';
import { forwardRef, useEffect, useRef, useState } from 'react';
import useChatRooms, { ChatRoomType, useChatState } from '@/hooks/useSocket/chat/useChatRooms';

import ChatConnectionStatus from '@/components/ChatWidget/ChatConnectionStatus';
import ChatList from '@/components/ChatWidget/ChatList';
import { Datetime } from '@/utils';
import HasPermission from '../HasPermission';
import { Link } from 'react-router-dom';
import Portal from '@/components/Portal';
import useInterval from '@/hooks/useInterval';
import useSocket from '@/hooks/useSocket';
import useViewport from '@/hooks/useViewport';
import useVisibility from '@/hooks/useVisibility';

const ChatWidget = () => (
  <HasPermission name="allowViewChat">
    <Portal>
      <div className="ChatWidget">
        <Chat />
      </div>
    </Portal>
  </HasPermission>
);

const Chat = () => {
  const socket = useSocket();
  const [, , { totalUnread, getRooms }] = useChatRooms(ChatRoomType.CURRENT_CHAT);
  const { rooms, config } = useChatState(({ state: { rooms, config } }) => ({ rooms: rooms?.[ChatRoomType.CURRENT_CHAT] || {}, config }));
  const setChatState = useChatState(({ setState }) => setState);
  const [hidden, setHidden] = useState(false);
  const canHide = HasPermission.check('allowHideChat');
  const headerRef = useRef(null);
  const [roomId, setRoomId] = useState(null);
  const retries = useRef(0);
  const [
    {
      content: { height: viewportHeight },
    },
  ] = useViewport();
  useInterval(() => {
    const now = Date.now();
    setChatState((current) => {
      const now = Date.now();
      return {
        ...current,
        usersTyping: current.usersTyping.filter((user: any) => now - user.timestamp < 3000),
      };
    });
  }, 3000);
  const headerHeight = headerRef.current?.offsetHeight || 0;
  const listHeight = !roomId ? viewportHeight * 0.5 : viewportHeight * 0.75;
  const maxHeight = listHeight - headerHeight;

  const getChatRooms = async (retry: boolean = true) =>
    getRooms({
      format: ChatRoomType.CURRENT_CHAT,
      start_datetime: new Datetime(TODAY).startOf('day').add(TIMEZONE_OFFSET, 'minutes').format(DATETIME_RFC_FORMAT),
      end_datetime: new Datetime(TODAY_EOD).endOf('day').add(TIMEZONE_OFFSET, 'minutes').format(DATETIME_RFC_FORMAT),
      user_provider_id: null,
      keyword: null,
    }).catch(() => {
      console.error(`Failed to get rooms.`);
    });

  const isVisible = useVisibility();
  useEffect(() => {
    if (!isVisible) return;
    getChatRooms();
  }, [isVisible]);

  useInterval(() => {
    if (!isVisible || !socket?.connected || roomId) return;
    getChatRooms(false);
  }, config?.client_refresh_interval_milliseconds ?? 5000);

  const lastRoomId = useRef<string>(null);
  useEffect(() => {
    if (lastRoomId.current === roomId) return;
    lastRoomId.current = roomId;
    getChatRooms(false);
  }, [roomId]);

  return (
    <>
      <ChatWidgetHeader unread={totalUnread} onClick={canHide ? () => setHidden(!hidden) : undefined} ref={headerRef} />
      {!hidden && <ChatList rooms={rooms} style={{ maxHeight }} onSelect={(roomId) => setRoomId(roomId)} height={listHeight} />}
    </>
  );
};

type ChatWidgetHeaderProps = {
  onClick?: () => void;
  unread?: number;
};
const ChatWidgetHeader = (() => {
  const ChatWidgetHeaderWithRef = ({ onClick, unread: _unread }: ChatWidgetHeaderProps, ref) => (
    <div
      className={`ChatWidget-Header position-sticky top-0 rounded-top-4 ps-5 pe-3 py-1 {z:999} ${onClick ? 'pointer' : ''}`}
      onClick={onClick}
      ref={ref}
    >
      <Stack direction="horizontal">
        <span className="ChatWidget-Title fs-5 text-uppercase">Message Center</span>
        <div className="ms-auto" />
        <ChatWidgetUnread unread={0} />
        <div className="ms-2">
          <ChatConnectionStatus />
        </div>
      </Stack>
    </div>
  );
  return forwardRef(ChatWidgetHeaderWithRef);
})();

type ChatWidgetUnreadProps = {
  unread?: number;
};
const ChatWidgetUnread = ({ unread }: ChatWidgetUnreadProps) => (
  <div className="ChatWidget-Unread">
    <Link to="/message-center">
      <span role="button">
        <i className="sv sv-inbox" />
      </span>
      {unread > 0 && (
        <Badge bg="secondary" pill>
          {unread}
        </Badge>
      )}
    </Link>
  </div>
);

export default ChatWidget;
